import i18n from 'i18next';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import MuiLink from '@mui/material/Link';
import { makeStyles } from 'tss-react/mui';
import { Link, useLocation } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import MapIcon from '@mui/icons-material/Map';
import PublicIcon from '@mui/icons-material/Public';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import NotificationsIcon from '@mui/icons-material/NotificationsNoneOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import PersonIcon from '@mui/icons-material/Person';
import BugIcon from '@mui/icons-material/BugReport';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import DevicesIcon from '@mui/icons-material/Devices';
import PlaceIcon from '@mui/icons-material/Place';
import { useTranslation } from 'react-i18next';
import { Button, MenuItem, Select, Stack } from '@mui/material';
import { useInboxMessages } from 'features/inboxMessages/hooks/useInboxMessages';
import { useCaptureDebugData } from 'hooks/useCaptureDebugData';
import { getResponsiveFontSize } from 'theme/watchdutyTheme.utils';
import { isIframe } from 'shared/utils';
import { useAuthState } from 'state/useAuthState';
import { Language, setLanguage } from 'i18n/utils';
import DonateIcon from 'assets/donate.svg?react';
import { Capacitor } from '@capacitor/core';
import { enqueueSnackbar } from 'notistack';
import {
  NOTIFICATIONS_REGIONS_QUERY_KEY,
  NOTIFICATIONS_SETTINGS_QUERY_KEY,
} from 'features/notifications/notifications.constants';
import FireCheckIcon from 'assets/fire_check.svg?react';
import { useColorScheme } from 'hooks/useColorScheme';
import { getPaywallLink } from 'features/membership/membership.utils';
import { WEATHER_ALERTS_LIST_CACHE_KEY } from 'features/weatherAlerts/weatherAlerts.constants';
import { useReleaseFlag, useReleaseFlagsLoading } from 'hooks/useReleaseFlags';
import { persistedQueryClient } from 'components/CacheableQueryClientProvider';
import { TextLogo } from 'components/TextLogo';
import { UserCard } from '../UserCard';
import { DeviceInfo } from './DeviceInfo';
import { ClearDataButton } from './ClearDataButton';

type DrawerNavProps = {
  open: boolean;
  toggleOpen: () => void;
  /**
   * Mount the drawer’s modal into this element instead of `document.body`.
   * Use in Storybook (or tests) so a portaled drawer stays under a scoped `ThemeProvider` subtree.
   */
  portalContainer?: HTMLElement | null;
};

const LANGUAGE_OPTIONS = [
  {
    value: 'en',
    transKey: 'drawerNav.language.values.en',
  },
  {
    value: 'es',
    transKey: 'drawerNav.language.values.es',
  },
  {
    value: 'tl',
    transKey: 'drawerNav.language.values.tl',
  },
  {
    value: 'vi',
    transKey: 'drawerNav.language.values.vi',
  },
  {
    value: 'zh',
    transKey: 'drawerNav.language.values.zh',
  },
];

const THEME_MODE_OPTIONS = [
  {
    value: 'system',
    transKey: 'drawerNav.colorScheme.values.system',
    icon: DevicesIcon,
  },
  {
    value: 'light',
    transKey: 'drawerNav.colorScheme.values.light',
    icon: LightModeIcon,
  },
  {
    value: 'dark',
    transKey: 'drawerNav.colorScheme.values.dark',
    icon: DarkModeIcon,
  },
] as const;

const useStyles = makeStyles()((theme) => {
  const { spacing } = theme;

  return {
    menuLogo: {
      // hard-coding to prevent iOS Dynamic Type from resizing this
      width: 168,
    },
    menuItem: {
      display: 'flex',
      height: '48px',
      '& .MuiListItemText-root span': {
        fontSize: getResponsiveFontSize(theme.typography.pxToRem(16)),
        lineHeight: getResponsiveFontSize(theme.typography.pxToRem(19)),
        fontWeight: 600,
      },
      '& .MuiListItemIcon-root': {
        width: 24,
        minWidth: 24,
        marginRight: 24,
      },
    },
    menu: {
      paddingTop: 'env(safe-area-inset-top)',
      paddingBottom: 'env(safe-area-inset-bottom)',
      paddingLeft: 'env(safe-area-inset-left)',
      width: '262px',
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      backgroundColor: theme.palette.background.paper,
      // iOS WKWebView only recognizes this as a scroll container with 'scroll'.
      // Elsewhere use 'auto' so the scrollbar appears only when content overflows.
      overflowY: Capacitor.getPlatform() === 'ios' ? 'scroll' : 'auto',
      WebkitOverflowScrolling: 'touch',
      touchAction: 'pan-y',
      overscrollBehavior: 'none',
      '& a': {
        textDecoration: 'none',
        color: theme.palette.text.primary,
        fontWeight: 'bold',
      },
      '& a:hover': {
        color: theme.palette.text.primary,
      },
    },
    badge: {
      marginRight: spacing(1),
    },
    languageItem: {
      margin: 0,
      paddingTop: 0,
      paddingBottom: 0,
      '& .MuiListItemIcon-root': {
        width: 24,
        minWidth: 24,
        marginRight: 24,
      },
    },
    footerContainer: {
      flex: 1,
      display: 'flex',
      alignItems: 'flex-end',
      position: 'relative',
    },
    link: {
      textDecoration: 'underline !important',
      fontWeight: 'normal !important' as 'normal',
    },
  };
});

const MenuLogo = (): JSX.Element => {
  const { classes } = useStyles();

  return (
    <Box p={2} pt={4} pb={3}>
      <TextLogo className={classes.menuLogo} />
    </Box>
  );
};

export const DrawerNav = (props: DrawerNavProps): JSX.Element => {
  const { open, toggleOpen, portalContainer } = props;
  const { t } = useTranslation();
  const { classes } = useStyles();
  const {
    isAuthenticated,
    hasActiveMembership,
    user,
    permissions: { canManageOrganization, isInternalUser },
    showSupportUs,
  } = useAuthState();
  const { unreadMessages } = useInboxMessages();
  const location = useLocation();
  const { captureDebugData } = useCaptureDebugData();
  const { mode, setMode } = useColorScheme();
  const flagsLoading = useReleaseFlagsLoading();
  const isDarkModeEnabled = useReleaseFlag('dark_mode');

  const handleCaptureDebugData = async (): Promise<void> => {
    toggleOpen();
    enqueueSnackbar(t('drawerNav.debugDataCaptureStarted'), {
      autoHideDuration: 3000,
    });
    await captureDebugData();
    enqueueSnackbar(t('drawerNav.debugDataCaptured'), {
      variant: 'success',
      autoHideDuration: 3000,
    });
  };

  // We want the notifications page to fully refresh on any link from the leftnav. This allows updates of regions
  // to propagate without disturbing the cache during normal user flows.
  // Not doing this on pageload because we toggle between the two views of edit and add counties.
  const clearNotificationSettingsCache = (): void => {
    persistedQueryClient.invalidateQueries({
      queryKey: [NOTIFICATIONS_SETTINGS_QUERY_KEY],
    });
    persistedQueryClient.invalidateQueries({
      queryKey: [NOTIFICATIONS_REGIONS_QUERY_KEY],
    });
    persistedQueryClient.invalidateQueries({
      queryKey: [WEATHER_ALERTS_LIST_CACHE_KEY],
    });

    toggleOpen();
  };

  const isMobileApp = Capacitor.isNativePlatform();
  const showNotifications = isMobileApp;
  const showInbox = isMobileApp;
  const showAccount = isAuthenticated;

  const paywallLink = getPaywallLink({
    hasActiveMembership,
    source: 'left_menu',
  });
  const newOrgLayers = useReleaseFlag('enable_external_layer_management');

  return (
    <Drawer
      classes={{ paper: classes.menu }}
      anchor="left"
      open={open}
      onClose={toggleOpen}
      disableScrollLock
      slotProps={{
        paper: {
          'aria-label': t('drawerNav.label'),
          // Prevents react-spring-bottom-sheet's document touchmove listener from blocking scroll on iOS
          onTouchMove: (e: React.TouchEvent<HTMLDivElement>) =>
            e.stopPropagation(),
        },
      }}
      {...(portalContainer ? { container: portalContainer } : {})}
    >
      <MenuLogo />

      <List disablePadding>
        <ListItemButton
          className={classes.menuItem}
          component={Link}
          to="/"
          onClick={toggleOpen}
        >
          <ListItemIcon>
            <MapIcon />
          </ListItemIcon>
          <ListItemText primary={t('drawerNav.map')} />
        </ListItemButton>

        {showInbox && (
          <ListItemButton
            className={classes.menuItem}
            component={Link}
            to="/inbox"
            onClick={toggleOpen}
          >
            <ListItemIcon>
              <InboxOutlinedIcon />
            </ListItemIcon>
            <ListItemText primary={t('drawerNav.inbox')} />
            <Badge
              badgeContent={unreadMessages}
              color="error"
              className={classes.badge}
            />
          </ListItemButton>
        )}

        <ListItemButton
          className={classes.menuItem}
          component={Link}
          to={{
            pathname:
              'https://support.watchduty.org/?utm_source=watch-duty-app',
          }}
          target="_blank"
          onClick={toggleOpen}
        >
          <ListItemIcon>
            <HelpOutlineIcon />
          </ListItemIcon>
          <ListItemText primary={t('drawerNav.help')} />
        </ListItemButton>

        {showSupportUs && (
          <>
            <ListItem>
              <ListItemText
                secondary={
                  <Typography
                    role="heading"
                    variant="subtitle1"
                    color="secondary"
                  >
                    {t('drawerNav.supportUs')}
                  </Typography>
                }
              />
            </ListItem>

            <ListItemButton
              className={classes.menuItem}
              component={Link}
              to={{
                pathname: paywallLink.pathname,
                search: paywallLink.search,
                state: {
                  fromRoute: location.pathname + location.search,
                },
              }}
              onClick={toggleOpen}
            >
              <ListItemIcon>
                <FireCheckIcon />
              </ListItemIcon>
              <ListItemText
                primary={
                  hasActiveMembership
                    ? t('drawerNav.yourMembership')
                    : t('drawerNav.membership')
                }
              />
            </ListItemButton>

            <ListItemButton
              className={classes.menuItem}
              component={Link}
              to="/donate"
              onClick={toggleOpen}
            >
              <ListItemIcon>
                <DonateIcon />
              </ListItemIcon>
              <ListItemText primary={t('drawerNav.donate')} />
            </ListItemButton>
          </>
        )}

        {canManageOrganization && (
          <>
            <ListItem>
              <ListItemText
                secondary={
                  <Typography
                    role="heading"
                    variant="subtitle1"
                    color="secondary"
                  >
                    {t('drawerNav.teamSettings')}
                  </Typography>
                }
              />
            </ListItem>

            <ListItemButton
              className={classes.menuItem}
              component={Link}
              to="/organization"
              onClick={toggleOpen}
            >
              <ListItemIcon>
                <PersonIcon />
              </ListItemIcon>
              <ListItemText primary={t('drawerNav.manageTeam')} />
            </ListItemButton>
            {newOrgLayers && (
              <ListItemButton
                className={classes.menuItem}
                component={Link}
                to="/organization/layers"
                onClick={toggleOpen}
              >
                <ListItemIcon>
                  <MapIcon />
                </ListItemIcon>
                <ListItemText primary={t('drawerNav.teamLayers')} />
              </ListItemButton>
            )}
          </>
        )}

        <ListItem>
          <ListItemText
            secondary={
              <Typography role="heading" variant="subtitle1" color="secondary">
                {t('drawerNav.settings')}
              </Typography>
            }
          />
        </ListItem>

        {showNotifications && (
          <ListItemButton
            className={classes.menuItem}
            component={Link}
            to={{
              pathname: '/notifications',
              // Pass current path since notifications page back navigation defaults to '/',
              // which makes the app lose the user's previous context.
              state: { fromRoute: location.pathname },
            }}
            onClick={clearNotificationSettingsCache}
            aria-label={t('drawerNav.notifications')}
          >
            <ListItemIcon>
              <NotificationsIcon />
            </ListItemIcon>
            <ListItemText primary={t('drawerNav.notifications')} />
          </ListItemButton>
        )}

        <ListItemButton
          className={classes.menuItem}
          component={Link}
          to="/places"
          onClick={toggleOpen}
        >
          <ListItemIcon>
            <PlaceIcon />
          </ListItemIcon>
          <ListItemText primary={t('drawerNav.places')} />
        </ListItemButton>

        <ListItem className={classes.languageItem}>
          <Select
            fullWidth
            value={i18n.resolvedLanguage}
            onChange={(e) => setLanguage(e.target.value as Language)}
            IconComponent={ExpandMoreIcon}
            renderValue={(value) => {
              const currentValue = LANGUAGE_OPTIONS.find(
                (el) => el.value === value,
              );
              return (
                <Stack
                  direction="row"
                  sx={{
                    alignItems: 'center',
                  }}
                >
                  <ListItemIcon>
                    <PublicIcon />
                  </ListItemIcon>
                  {currentValue && t(currentValue.transKey)}
                </Stack>
              );
            }}
            inputProps={{ 'aria-label': t('drawerNav.language.label') }}
            sx={{
              '.MuiInputBase-input': { padding: '10px 0' },
              '.MuiOutlinedInput-notchedOutline': { border: 'none' },
              fontWeight: 600,
            }}
          >
            {LANGUAGE_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {t(option.transKey)}
              </MenuItem>
            ))}
          </Select>
        </ListItem>

        {!flagsLoading && isDarkModeEnabled && (
          <ListItem className={classes.languageItem}>
            <Select
              fullWidth
              value={mode || 'system'}
              onChange={(e) =>
                setMode(e.target.value as 'system' | 'light' | 'dark')
              }
              IconComponent={ExpandMoreIcon}
              renderValue={(value) => {
                const currentOption = THEME_MODE_OPTIONS.find(
                  (option) => option.value === value,
                );
                const IconComponent = currentOption?.icon || DevicesIcon;
                return (
                  <Stack
                    direction="row"
                    sx={{
                      alignItems: 'center',
                      fontWeight: 600,
                    }}
                  >
                    <ListItemIcon>
                      <IconComponent />
                    </ListItemIcon>
                    {currentOption && t(currentOption.transKey)}
                  </Stack>
                );
              }}
              inputProps={{ 'aria-label': t('drawerNav.colorScheme.label') }}
              sx={{
                '.MuiInputBase-input': { padding: '10px 0' },
                '.MuiOutlinedInput-notchedOutline': { border: 'none' },
              }}
            >
              {THEME_MODE_OPTIONS.map((option) => {
                const OptionIcon = option.icon;
                return (
                  <MenuItem key={option.value} value={option.value}>
                    <ListItemIcon>
                      <OptionIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{t(option.transKey)}</ListItemText>
                  </MenuItem>
                );
              })}
            </Select>
          </ListItem>
        )}

        {isInternalUser && (
          <>
            <ListItem>
              <ListItemText
                secondary={
                  <Typography
                    role="heading"
                    variant="subtitle1"
                    color="secondary"
                  >
                    {t('drawerNav.debug')}
                  </Typography>
                }
              />
            </ListItem>
            <ListItemButton
              className={classes.menuItem}
              onClick={handleCaptureDebugData}
            >
              <ListItemIcon>
                <BugIcon />
              </ListItemIcon>
              <ListItemText
                secondary={
                  <Typography sx={{ fontWeight: 600 }}>
                    {t('drawerNav.captureDebugData')}
                  </Typography>
                }
              />
            </ListItemButton>
          </>
        )}
      </List>

      <Box className={classes.footerContainer}>
        <List
          disablePadding
          sx={{ width: '100%', paddingBottom: isMobileApp ? 2 : 0 }}
        >
          {!isIframe() && !isAuthenticated && (
            <ListItem>
              <Button
                fullWidth
                component={Link}
                to="/login"
                onClick={toggleOpen}
                size="large"
                sx={{ textAlign: 'center' }}
                color="neutral"
              >
                {t('drawerNav.login')}
              </Button>
            </ListItem>
          )}

          {showAccount && (
            <UserCard
              user={user}
              toggleOpen={toggleOpen}
              isMember={hasActiveMembership}
            />
          )}

          <ListItem>
            <Stack spacing={1} sx={{ width: '100%' }} alignItems="flex-start">
              <Stack sx={{ width: '100%' }} spacing={1}>
                <MuiLink
                  href="https://www.watchduty.org/?utm_source=watch-duty-app"
                  target="_blank"
                  className={classes.link}
                  variant="subtitle1"
                  onClick={toggleOpen}
                >
                  {t('drawerNav.about')}
                </MuiLink>

                <DeviceInfo sx={{ color: 'text.secondary' }} />
              </Stack>

              <ClearDataButton />
            </Stack>
          </ListItem>
        </List>
      </Box>
    </Drawer>
  );
};
